import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Modal, Badge, Pagination } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllCompensatoryLeavesForApprover, updateCompensatoryLeaveStatus, getCompLeaveDocument } from "../../../../api/onboarddeboard";

const ITEMS_PER_PAGE = 20;

const validateRemark = (value) => {
  const trimmed = value.trim();
  if (/^\s+$/.test(value)) return "Remark cannot contain only empty spaces";
  if (!trimmed) return "Remark is required";
  if (/^\d+$/.test(trimmed)) return "Remark cannot contain only numbers";
  if (/^[^a-zA-Z0-9]+$/.test(trimmed)) return "Remark cannot contain only special characters";
  if (/^[^a-zA-Z]*$/.test(trimmed) && /[0-9]/.test(trimmed) && /[^a-zA-Z0-9]/.test(trimmed))
    return "Remark cannot contain only numbers and special characters";
  if (trimmed.length > 500) return "Remark cannot exceed 500 characters.";
  return "";
};

const CompensatoryApproval = () => {
  const token = localStorage.getItem("token");
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("applyDate");
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docUrl, setDocUrl] = useState(null);
  const [docType, setDocType] = useState("");

  const [showWorkModal, setShowWorkModal] = useState(false);
  const [workDetailsText, setWorkDetailsText] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    getAllCompensatoryLeavesForApprover(token)
      .then((r) => setPending(r.data || []))
      .catch(() => toast.error("Failed to fetch pending compensatory requests."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  let filtered = [...pending];
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter((l) => (l.employeeName || "").toLowerCase().includes(q) || (l.workDetails || "").toLowerCase().includes(q));
  }
  if (sortField === "employee") filtered.sort((a, b) => (a.employeeName || "").localeCompare(b.employeeName || ""));
  else if (sortField === "applyDate") filtered.sort((a, b) => new Date(b.applyDate || 0) - new Date(a.applyDate || 0));

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const openModal = (req, act) => { setSelected(req); setAction(act); setRemark(""); setRemarkError(""); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setSelected(null); setAction(""); setRemark(""); setRemarkError(""); };

  const handleRemarkChange = (e) => {
    const value = e.target.value;
    setRemark(value);
    setRemarkError(value ? validateRemark(value) : "");
  };

  const handleSubmit = async () => {
    const err = validateRemark(remark);
    if (err) { setRemarkError(err); return; }
    setSubmitting(true);
    try {
      await updateCompensatoryLeaveStatus(selected.id, { status: action === "APPROVE" ? "APPROVED" : "REJECTED", approverRemark: remark.trim() }, token);
      toast.success(action === "APPROVE" ? "Compensatory leave approved successfully" : "Compensatory leave rejected successfully");
      closeModal();
      load();
    } catch (err) {
      toast.error(err?.response?.data || "Failed to process request.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDocument = async (id) => {
    try {
      const res = await getCompLeaveDocument(id, token);
      setDocUrl(URL.createObjectURL(res.data));
      setDocType(res.data.type);
      setShowDocModal(true);
    } catch {
      toast.error("Failed to load document.");
    }
  };
  const closeDocModal = () => { if (docUrl) URL.revokeObjectURL(docUrl); setDocUrl(null); setDocType(""); setShowDocModal(false); };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

  return (
    <Container fluid className="p-4">
      <style>{`
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
      `}</style>
      <ToastContainer position="top-right" autoClose={3000} />

      <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Compensatory Request Approval</h4>
      <Breadcrumb className="lc-breadcrumb">
        <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
        <Breadcrumb.Item href="#">Leave Application</Breadcrumb.Item>
        <Breadcrumb.Item active>Compensatory Request Approval</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">Pending Compensatory Leave Applications</h5>
              <Badge bg="secondary">Total: {filtered.length}</Badge>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3 justify-content-end align-items-center g-2">
                <Col xs="auto">
                  <Form.Control placeholder="Search by Employee / Work Details..." value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} style={{ width: 250 }} />
                </Col>
                <Col xs="auto">
                  <Form.Select value={sortField} onChange={(e) => setSortField(e.target.value)} style={{ width: 190 }}>
                    <option value="applyDate">Sort: Latest First</option>
                    <option value="employee">Sort: Employee (A–Z)</option>
                  </Form.Select>
                </Col>
              </Row>

              <Table bordered hover responsive>
                <thead>
                  <tr>
                    {["Sr.No", "Employee", "Working Date", "Applied On", "Work Details", "Document", "Action"].map((h) => (
                      <th key={h} style={{ backgroundColor: "#2E5CB8", color: "white" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="text-center py-4"><div className="text-muted">Loading...</div></td></tr>}
                  {!loading && paginated.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-4"><div className="text-muted">No pending compensatory leave applications</div></td></tr>
                  )}
                  {!loading && paginated.map((r, index) => (
                    <tr key={r.id}>
                      <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                      <td>{r.employeeName || "-"}</td>
                      <td>{formatDate(r.workingDate)}</td>
                      <td>{formatDate(r.applyDate)}</td>
<td style={{ maxWidth: 200 }}>
  <div className="d-flex align-items-center">
    <span
      style={{ minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
      title={r.workDetails}
    >
      {r.workDetails || "-"}
    </span>
    {r.workDetails && r.workDetails.length > 20 && (
      <Button
        variant="link"
        size="sm"
        className="p-0 ms-1 text-danger"
        style={{ fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}
        onClick={() => { setWorkDetailsText(r.workDetails); setShowWorkModal(true); }}
      >
        more
      </Button>
    )}
  </div>
</td>                      <td>
                        {r.documentName ? (
                          <Button variant="link" size="sm" className="p-0" onClick={() => handleViewDocument(r.id)}>
                            <i className="bi bi-file-earmark-text fs-5" style={{ color: "#2E5CB8" }} />
                          </Button>
                        ) : "—"}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <Button variant="success" size="sm" className="me-1" style={{ padding: "3px 8px", fontSize: 12 }} onClick={() => openModal(r, "APPROVE")}>Approve</Button>
                        <Button variant="danger" size="sm" style={{ padding: "3px 8px", fontSize: 12 }} onClick={() => openModal(r, "REJECT")}>Reject</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-end">
                  <Pagination>
                    <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {[...Array(totalPages).keys()].map((n) => (
                      <Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>{n + 1}</Pagination.Item>
                    ))}
                    <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {action === "APPROVE" ? (<><i className="bi bi-check-circle-fill text-success me-2" />Approve Compensatory Leave</>) : (<><i className="bi bi-x-circle-fill text-danger me-2" />Reject Compensatory Leave</>)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", fontSize: 14 }}>
                <Row><Col sm={5} className="text-muted fw-semibold">Employee:</Col><Col sm={7}>{selected.employeeName || "-"}</Col></Row>
                <Row className="mt-1"><Col sm={5} className="text-muted fw-semibold">Working Date:</Col><Col sm={7}>{formatDate(selected.workingDate)}</Col></Row>
                <Row className="mt-1"><Col sm={5} className="text-muted fw-semibold">Applied On:</Col><Col sm={7}>{formatDate(selected.applyDate)}</Col></Row>
                <Row className="mt-1"><Col sm={5} className="text-muted fw-semibold">Work Details:</Col><Col sm={7}>{selected.workDetails || "-"}</Col></Row>
              </div>
              <Form.Group>
                <Form.Label>Remark <span className="text-danger">*</span></Form.Label>
                <Form.Control as="textarea" rows={4} maxLength={500} value={remark} onChange={handleRemarkChange} placeholder="Enter remark" />
                <div className="d-flex justify-content-between mt-1">
                  <span style={{ color: "red", fontSize: 13 }}>{remarkError}</span>
                  <small className="text-muted">{remark.length}/500</small>
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeModal}>Cancel</Button>
          <Button variant={action === "APPROVE" ? "success" : "danger"} size="sm" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Processing..." : action === "APPROVE" ? "Approve" : "Reject"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showWorkModal} onHide={() => setShowWorkModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Work Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "60vh", overflowY: "auto" }}>
          {workDetailsText}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowWorkModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
      
      <Modal show={showDocModal} onHide={closeDocModal} size="lg" centered>
        <Modal.Header closeButton><Modal.Title><i className="bi bi-file-earmark-text me-2" />Document Viewer</Modal.Title></Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {docUrl && (docType.startsWith("image/") ? (
            <img src={docUrl} alt="Document" style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain" }} />
          ) : (
            <iframe src={docUrl} style={{ width: "100%", height: "60vh", border: "none" }} title="Document Viewer" />
          ))}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={closeDocModal}>Close</Button></Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CompensatoryApproval;